import {Injectable, OnDestroy} from '@angular/core';
import {Router} from '@angular/router';
import {MatSnackBar, MatSnackBarConfig} from '@angular/material/snack-bar';
import {Subscription} from 'rxjs';
import {SocketClusterService} from '@core/services/socket-cluster.service';
import {CalendarReminderEvent} from '@shared/models/web-sockets/web-socket';
import {
  CalendarReminderToastComponent, CalendarReminderToastData
} from '@shared/components/snack-bar-components/calendar-reminder-toast/calendar-reminder-toast.component';

/**
 * ITP-E-337-05 (Plan ITP-P-73, story 14) — the in-app reminder ten minutes before a meeting. The Scheduler sends everyone on
 * the meeting who has not answered No a CalendarReminder (ITP-E-337-02) on their own channel; this shows it as a 30-second
 * toast, "<title> starts in 10 min", with Join (the meeting's Meet room, `/meet/<code>`) and Dismiss.
 *
 * - One toast per occurrence for the page's life, keyed `eventId|occurrenceStartUtc`: the personal channel replays what it
 *   missed after a drop, and a copy never shows twice.
 * - Not shown inside that meeting's own room, `/meet/<code>`, matched ignoring case. Being in any other call or meeting does
 *   not hold it back. A reminder held back there still counts as seen, so leaving the room does not bring it back.
 * - A reminder whose meeting started more than ten minutes ago (a replay after a long drop) is too late, and is not shown.
 *
 * Root-provided, and started by the platform shell once the user is known, beside the CallService. The standalone Meet app
 * and Connect do not start it.
 */
@Injectable({providedIn: 'root'})
export class CalendarReminderService implements OnDestroy {
  static readonly durationMs = 30000;
  /** How long before an occurrence the server sends its reminder (CalendarEventReminder.Lead). */
  static readonly leadMinutes = 10;
  /** Meet's own pages under `/meet/`, which are not meeting codes (orbitaxMeetShellRoutes). */
  static readonly reservedMeetSegments = ['messages', 'calendar', 'meeting-planner', 'meetings'];

  private readonly seen = new Set<string>();
  private messages: Subscription | null = null;

  constructor(private socketClusterService: SocketClusterService,
              private snackBar: MatSnackBar,
              private router: Router) {
  }

  /** Listens for reminders from now on. A second call does nothing. */
  start(): void {
    if (this.messages) {
      return;
    }
    this.messages = this.socketClusterService.onCalendarReminderMessage().subscribe(reminder => this.onReminder(reminder));
  }

  ngOnDestroy(): void {
    this.messages?.unsubscribe();
  }

  /** The meeting code of the Meet room `url` is on (`/meet/<code>`, any query or fragment), else null. */
  static meetRoomCode(url: string | null | undefined): string | null {
    const match = /^\/meet\/([^/?#;]+)\/?(?:[?#;].*)?$/i.exec(url ?? '');
    if (!match) {
      return null;
    }
    let code = match[1];
    try {
      code = decodeURIComponent(code);
    } catch {
      // A malformed escape: compare the segment as it is.
    }
    return CalendarReminderService.reservedMeetSegments.includes(code.toLowerCase()) ? null : code;
  }

  /** Whether `url` is the Meet room of the meeting whose code is `meetCode`, ignoring case. */
  static isInRoom(url: string | null | undefined, meetCode: string | null | undefined): boolean {
    const room = CalendarReminderService.meetRoomCode(url);
    const code = meetCode?.trim();
    return !!room && !!code && room.toLowerCase() === code.toLowerCase();
  }

  /**
   * "starts in N min", from the occurrence's start; never more than the lead, so a browser clock running behind does not say
   * "starts in 13 min". "is starting now" once the start has come; the lead when the start cannot be read.
   */
  static when(occurrenceStartUtc: string, now: number): string {
    const minutes = Math.ceil((Date.parse(occurrenceStartUtc) - now) / 60000);
    if (Number.isNaN(minutes)) {
      return `starts in ${CalendarReminderService.leadMinutes} min`;
    }
    return minutes < 1 ? 'is starting now' : `starts in ${Math.min(minutes, CalendarReminderService.leadMinutes)} min`;
  }

  private onReminder(reminder: CalendarReminderEvent): void {
    const key = `${reminder.eventId}|${reminder.occurrenceStartUtc}`;
    if (this.seen.has(key)) {
      return;
    }
    this.seen.add(key);
    const now = Date.now();
    if (Date.parse(reminder.occurrenceStartUtc) < now - CalendarReminderService.leadMinutes * 60000) {
      console.info('[calendar-reminder] Not showing a reminder for a meeting that started a while ago', key);
      return;
    }
    if (CalendarReminderService.isInRoom(this.router.url, reminder.meetCode)) {
      return;
    }
    this.show(reminder, now);
  }

  private show(reminder: CalendarReminderEvent, now: number): void {
    const meetCode = reminder.meetCode?.trim();
    const config = new MatSnackBarConfig<CalendarReminderToastData>();
    config.duration = CalendarReminderService.durationMs;
    config.horizontalPosition = 'right';
    config.verticalPosition = 'bottom';
    config.panelClass = [CalendarReminderToastComponent.panelClass];
    config.data = {
      title: reminder.title?.trim() || 'Your meeting',
      when: CalendarReminderService.when(reminder.occurrenceStartUtc, now),
      canJoin: !!meetCode
    };
    const toast = this.snackBar.openFromComponent(CalendarReminderToastComponent, config);
    toast.onAction().subscribe(() => {
      this.router.navigate(['/meet', meetCode]).catch(error =>
        console.warn('[calendar-reminder] Opening the Meet room failed', error));
    });
  }
}
